import { FC } from 'react';
import * as Yup from 'yup';
import { navigate } from 'gatsby';
import { ButtonOutlinePrimary, Form, InputRadio, InputRadioGroup, InputTextarea } from '@lambdacurry/component-library';
import { useCancelSubscriptionMutation } from 'lib/home/generated/queries';
import { CancellationReasons, OrdersQuery, SubscriptionStatus } from 'lib/home/generated/types/api';
import { useQueryClient } from 'react-query';

export interface CancelPlanReasonFormValues {
  reason: CancellationReasons;
  feedback?: string;
}

export const CancelPlanReasonForm: FC = () => {
  const queryClient = useQueryClient();
  const { mutate: cancelSubscription } = useCancelSubscriptionMutation();

  const handleSubmit = (values: CancelPlanReasonFormValues) => {
    if (values.reason === CancellationReasons.TooExpensive || values.reason === CancellationReasons.TooMany) {
      navigate('/account/cancel/suggestions', { state: { cancellation: values } });
      return;
    }

    cancelSubscription(
      {},
      {
        onSuccess: () => {
          queryClient.setQueryData(['Orders'], (query: OrdersQuery) => {
            query.me.subscription.status = SubscriptionStatus.Cancelled;
            return query;
          });

          navigate('/account/cancel/confirm', { state: { cancellation: values } });
        }
      }
    );
  };

  return (
    <Form<CancelPlanReasonFormValues>
      initialValues={{ reason: undefined, feedback: '' }}
      onSubmit={handleSubmit}
      validationSchema={Yup.object().shape({
        reason: Yup.string().required('This is a required field').typeError('Please select a reason')
      })}
    >
      {formikProps => (
        <>
          <InputRadioGroup name="reason" formikProps={formikProps}>
            <InputRadio label="Receiving food too often" value={CancellationReasons.TooMany} />
            <InputRadio label="It’s too expensive" value={CancellationReasons.TooExpensive} />
            <InputRadio label="I’m not using the products enough" value={CancellationReasons.NotUsing} />
            <InputRadio label="I’m not liking the dishes" value={CancellationReasons.DontLike} />
            <InputRadio label="Too complicated for me" value={CancellationReasons.TooComplicated} />
            <InputRadio label="Other" value={CancellationReasons.Other} />
          </InputRadioGroup>

          {[CancellationReasons.Other, CancellationReasons.DontLike].includes(formikProps.values.reason) && (
            <>
              <h6 className="mt-6">Anything That Could Be Improved</h6>
              <InputTextarea name="feedback" formikProps={formikProps} />
            </>
          )}

          {formikProps.errors.reason && <p className="text-danger">{formikProps.errors.reason}</p>}

          <ButtonOutlinePrimary className="mt-2" disabled={!formikProps.values.reason} type="submit">
            Continue
          </ButtonOutlinePrimary>
        </>
      )}
    </Form>
  );
};
